import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  LinearProgress,
  Paper,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import MetaTags from '../components/MetaTags';
import SuccessDialog from '../components/SuccessDialog';
import { getApiUrl } from '../utils/apiUrl';

const PasswordResetComplete: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get token from URL
  const token = searchParams.get('token');
  
  // Form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState<{email: string, username: string, accountType: string} | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Validate token on component mount
  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenValid(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/api/password-reset/validate/${token}`);
      
      if (response.data.success) {
        setTokenValid(true);
        setUserInfo(response.data.data);
      } else {
        setTokenValid(false);
      }
    } catch (error: any) {
      console.error('Token validation error:', error);
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  // Password validation
  const validatePassword = (password: string) => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('at least 8 characters');
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('one lowercase letter');
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('one uppercase letter');
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push('one number');
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.push('one special character (@$!%*?&)');
    }
    
    return errors;
  };

  // Form validation
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      newErrors.newPassword = `Password must contain ${passwordErrors.join(', ')}`;
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${getApiUrl()}/api/password-reset/complete`, {
        token,
        newPassword
      });

      if (response.data.success) {
        setSuccess(true);
        setSuccessDialogConfig({
          title: 'Password Reset Successfully',
          message: `Your password has been reset successfully! You can now log in with your new password using your ${userInfo?.accountType || 'account'} credentials.`,
          autoCloseDelay: 5000,
        });
        setShowSuccessDialog(true);
        
        // Redirect to thank you page after 3 seconds
        setTimeout(() => {
          navigate('/thank-you?type=password-reset');
        }, 3000);
      } else {
        throw new Error(response.data.message || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('Password reset completion error:', error);
      
      if (error.response?.status === 429) {
        setSuccessDialogConfig({
          title: 'Too Many Attempts',
          message: 'Too many attempts. Please wait 15 minutes before trying again.',
          autoCloseDelay: 5000,
        });
        setShowSuccessDialog(true);
      } else if (error.response?.status === 400) {
        setSuccessDialogConfig({
          title: 'Invalid Token',
          message: 'Invalid or expired reset token. Please request a new password reset.',
          autoCloseDelay: 5000,
        });
        setShowSuccessDialog(true);
        setTimeout(() => navigate('/password-reset'), 2000);
      } else {
        setSuccessDialogConfig({
          title: 'Reset Failed',
          message: error.response?.data?.message || 'Failed to reset password. Please try again.',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validating) {
    return (
      <>
        <MetaTags
          title="Validating Reset Token - LeadForge"
          description="Validating your password reset token. Please wait while we verify your request."
        />
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <SecurityIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Validating Reset Token
              </Typography>
              <LinearProgress sx={{ mt: 3 }} />
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <>
        <MetaTags
          title="Invalid Reset Token - LeadForge"
          description="The password reset link you used is invalid or has expired. Please request a new reset link."
        />
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              color: 'white'
            }}
          >
            <ErrorIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Invalid Reset Link
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              This password reset link is invalid or has expired.
            </Typography>
          </Paper>
          
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Reset links expire after 1 hour for security reasons, or become invalid once used.
                </Typography>
              </Alert>
              
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  component={Link}
                  to="/password-reset"
                  variant="contained"
                  size="large"
                  sx={{ mr: 2 }}
                >
                  Request New Reset Link
                </Button>
                <Button
                  component={Link}
                  to="/admin/login"
                  variant="outlined"
                  size="large"
                >
                  Back to Login
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  // Success state
  if (success) {
    return (
      <>
        <MetaTags 
          title="Password Reset Successful - LeadForge"
          description="Your password has been reset successfully. You can now log in to your LeadForge account with your new password."
        />
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #00b894 0%, #00cec9 100%)',
              color: 'white'
            }}
          >
            <SuccessIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Password Reset Successful!
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Your password has been updated successfully.
            </Typography>
          </Paper>
          
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  You can now log in to your {userInfo?.accountType} account using your new password.
                </Typography>
              </Alert>
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Redirecting to login in 3 seconds...
                </Typography>
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  size="large"
                  sx={{
                    background: 'linear-gradient(135deg, #00b894 0%, #00cec9 100%)',
                  }}
                >
                  Go to Login Now
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  // Main reset form
  return (
    <>
      <MetaTags 
        title="Set New Password - LeadForge"
        description="Set your new password to complete the reset process."
      />
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            mb: 3
          }}
        >
          <LockIcon sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Set New Password
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {userInfo && (
              <>Welcome back, <strong>{userInfo.username}</strong>. Please choose a secure new password.</>
            )}
          </Typography>
        </Paper>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Password Requirements:</strong>
                  <br />• At least 8 characters long
                  <br />• One uppercase and one lowercase letter
                  <br />• One number and one special character (@$!%*?&)
                </Typography>
              </Alert>

              {loading && (
                <Box sx={{ mb: 3 }}>
                  <LinearProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    Updating your password...
                  </Typography>
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !newPassword || !confirmPassword}
                sx={{ 
                  mb: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              >
                {loading ? 'Updating Password...' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            <Button component={Link} to="/login" variant="text" size="small">
              Back to Login
            </Button>
          </Typography>
        </Box>

        {/* Success Dialog */}
        <SuccessDialog
          open={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          title={successDialogConfig.title}
          message={successDialogConfig.message}
          buttonText="Got it"
          autoCloseDelay={3000}
        />
      </Container>
    </>
  );
};

export default PasswordResetComplete;